/**
 * AgentDashboard Component
 * AIDLC Administrative Backend
 *
 * Landing page / home view.
 * Displays all agents in a responsive grid of AgentStatusCards.
 * Grid: 3 columns on xl, 2 on md, 1 on mobile.
 *
 * Per the Architecture Document, this is Module 01: Agent Dashboard.
 * Shows real-time agent status with summary statistics at the top.
 *
 * Bridge Integration:
 *   When the bridge is active, agent statuses update in real-time
 *   via WebSocket events pushed from the bridge file watcher.
 *   The LIVE badge and "last updated" timestamp reflect bridge activity.
 *
 * Status values aligned with AgentStatus type:
 *   'working' | 'available' | 'waiting' | 'away'
 */

import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgents } from '../../hooks/useAgents';
import { useAgentGroups } from '../../hooks/useAgentGroups';
import { useAgentSessionSummary } from '../../hooks/useAgentSessionSummary';
import { useBridge } from '../layout/MainLayout';
import { DispatcherToggle } from './DispatcherToggle';
import { AgentStatusCard } from './AgentStatusCard';
import { AgentGroupSection } from './AgentGroupSection';
import { AgentGroupModal } from './AgentGroupModal';
import { ApprovalPanel } from './ApprovalPanel';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import {
  createAgentGroup,
  updateAgentGroup,
  deleteAgentGroup,
  updateGroupAgents,
  type AgentGroup,
} from '../../services/agentGroupService';
import type { AgentStatus } from '../../types/agent';
import { Grid, FolderOpen, Plus } from 'lucide-react';

type FilterOption = 'all' | AgentStatus;
type ViewMode = 'grid' | 'groups';

export function AgentDashboard() {
  const navigate = useNavigate();
  // Every agent card opens the agent's profile, in Grid and Groups views alike (PORTAL-BUG-A).
  const openAgent = (agentId: string) => navigate(`/agents/${agentId}`);
  const { socket } = useBridge();
  const { agents, isLoading, error, refresh, lastBridgeUpdate, bridgeActive } = useAgents(
    30000, // 30s poll
    socket,
  );
  const { groups, refresh: refreshGroups } = useAgentGroups(socket);
  const sessionSummaries = useAgentSessionSummary(socket);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AgentGroup | null>(null);

  const summary = useMemo(() => {
    const working = agents.filter((a) => a.status === 'working').length;
    const available = agents.filter((a) => a.status === 'available').length;
    const waiting = agents.filter((a) => a.status === 'waiting').length;
    const away = agents.filter((a) => a.status === 'away').length;
    const offline = agents.filter((a) => a.status === 'offline').length;
    const totalTokens = agents.reduce((sum, a) => sum + a.statistics.totalTokensUsed, 0);
    return { working, available, waiting, away, offline, total: agents.length, totalTokens };
  }, [agents]);

  const filteredAgents = useMemo(() => {
    let result = agents;
    if (filter !== 'all') {
      result = result.filter((a) => a.status === filter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.role.toLowerCase().includes(q) ||
          a.expertise.some((e) => e.toLowerCase().includes(q))
      );
    }
    return result;
  }, [agents, filter, searchQuery]);

  // Group agents by their group membership
  const groupedAgents = useMemo(() => {
    const result: Record<string, typeof agents> = {};
    const ungrouped: typeof agents = [];

    // Initialize groups
    groups.forEach((g) => {
      result[g.id] = [];
    });

    // Assign agents to groups
    filteredAgents.forEach((agent) => {
      let assigned = false;
      for (const group of groups) {
        if (group.agentIds.includes(agent.id)) {
          result[group.id].push(agent);
          assigned = true;
          break; // Agent can only be in one group
        }
      }
      if (!assigned) {
        ungrouped.push(agent);
      }
    });

    return { byGroup: result, ungrouped };
  }, [agents, groups, filteredAgents]);

  // Handlers for group operations
  const handleCreateGroup = useCallback(async () => {
    setEditingGroup(null);
    setModalOpen(true);
  }, []);

  const handleEditGroup = useCallback((group: AgentGroup) => {
    setEditingGroup(group);
    setModalOpen(true);
  }, []);

  const handleSaveGroup = useCallback(
    async (groupData: Partial<AgentGroup>) => {
      try {
        if (editingGroup) {
          await updateAgentGroup(editingGroup.id, groupData);
        } else {
          await createAgentGroup(groupData as any);
        }
        await refreshGroups();
      } catch (err) {
        console.error('Failed to save group:', err);
        throw err;
      }
    },
    [editingGroup, refreshGroups]
  );

  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      if (!confirm('Delete this group? Agents will be moved to Ungrouped.')) return;
      try {
        await deleteAgentGroup(groupId);
        await refreshGroups();
      } catch (err) {
        console.error('Failed to delete group:', err);
        alert('Failed to delete group. Please try again.');
      }
    },
    [refreshGroups]
  );

  const handleToggleCollapse = useCallback(
    async (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;
      try {
        await updateAgentGroup(groupId, { isCollapsed: !group.isCollapsed });
        await refreshGroups();
      } catch (err) {
        console.error('Failed to toggle collapse:', err);
      }
    },
    [groups, refreshGroups]
  );

  const handleDrop = useCallback(
    async (targetGroupId: string | null, agentId: string) => {
      try {
        // Remove agent from all groups first
        for (const group of groups) {
          if (group.agentIds.includes(agentId)) {
            const newAgentIds = group.agentIds.filter((id) => id !== agentId);
            await updateGroupAgents(group.id, newAgentIds);
          }
        }

        // Add to target group if specified
        if (targetGroupId) {
          const targetGroup = groups.find((g) => g.id === targetGroupId);
          if (targetGroup) {
            const newAgentIds = [...targetGroup.agentIds, agentId];
            await updateGroupAgents(targetGroupId, newAgentIds);
          }
        }

        await refreshGroups();
      } catch (err) {
        console.error('Failed to update agent group:', err);
        alert('Failed to move agent. Please try again.');
      }
    },
    [groups, refreshGroups]
  );

  if (isLoading) {
    return <LoadingSpinner message="Loading agents..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700 text-sm font-medium">Failed to load agents</p>
        <p className="text-red-600 text-xs mt-1">{error}</p>
        <button
          onClick={refresh}
          className="mt-3 px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Approval Requests */}
      <ApprovalPanel />

      {/* Bridge Status Banner */}
      {bridgeActive && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          <span className="text-xs text-green-700 font-medium">
            Bridge Active -- Agent statuses update in real-time from Claude Code sessions
          </span>
          {lastBridgeUpdate && (
            <span className="text-xs text-green-500 ml-auto">
              Last activity: {new Date(lastBridgeUpdate).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      {/* Summary Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-black">{summary.total}</div>
          <div className="text-xs text-gray-500 mt-1">Total Agents</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{summary.working}</div>
          <div className="text-xs text-gray-500 mt-1">Working</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-blue">{summary.available}</div>
          <div className="text-xs text-gray-500 mt-1">Available</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600">{summary.waiting}</div>
          <div className="text-xs text-gray-500 mt-1">Waiting</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-400">{summary.offline}</div>
          <div className="text-xs text-gray-500 mt-1">Offline</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 border border-gray-200 rounded-md">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-2 text-xs font-medium rounded-l-md transition-colors flex items-center gap-1.5 ${
              viewMode === 'grid'
                ? 'bg-ldaja-blue text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title="Grid view"
          >
            <Grid className="w-4 h-4" />
            Grid
          </button>
          <button
            onClick={() => setViewMode('groups')}
            className={`px-3 py-2 text-xs font-medium rounded-r-md transition-colors flex items-center gap-1.5 ${
              viewMode === 'groups'
                ? 'bg-ldaja-blue text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title="Groups view"
          >
            <FolderOpen className="w-4 h-4" />
            Groups
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-1 w-full sm:max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
          />
        </div>

        {/* Status Filters */}
        <div className="flex items-center gap-1">
          {([
            { key: 'all', label: 'All', count: summary.total },
            { key: 'working', label: 'Working', count: summary.working },
            { key: 'available', label: 'Available', count: summary.available },
            { key: 'waiting', label: 'Waiting', count: summary.waiting },
            { key: 'away', label: 'Away', count: summary.away },
            { key: 'offline', label: 'Offline', count: summary.offline },
          ] as const).map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === key
                  ? 'bg-ldaja-blue text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        {/* Chat service (Agent Dispatcher) on/off. Pushed right, so the row reads
            left to right as: what you are looking at, then what is running. */}
        <div className="ml-auto">
          <DispatcherToggle socket={socket} />
        </div>

        {/* Refresh */}
        <button
          onClick={refresh}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          title="Refresh agent data"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Agent Grid or Groups View */}
      {viewMode === 'grid' ? (
        // Grid View
        filteredAgents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredAgents.map((agent) => (
              <AgentStatusCard key={agent.id} agent={agent} sessionSummary={sessionSummaries[agent.id]} onClick={openAgent} />
            ))}
          </div>
        ) : agents.length === 0 ? (
          <EmptyState
            title="No agents registered"
            description="Configure agents in Settings to get started with the AIDLC system."
          />
        ) : (
          <EmptyState
            title="No agents match your filters"
            description="Try adjusting your search query or status filter."
            action={
              <button
                onClick={() => {
                  setFilter('all');
                  setSearchQuery('');
                }}
                className="text-sm text-ldaja-blue hover:underline"
              >
                Clear filters
              </button>
            }
          />
        )
      ) : (
        // Groups View
        <div>
          {/* New Group Button */}
          <div className="mb-6">
            <button
              onClick={handleCreateGroup}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-ldaja-blue rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Group
            </button>
          </div>

          {/* Agent Groups */}
          {groups.map((group) => (
            <AgentGroupSection
              key={group.id}
              group={group}
              agents={groupedAgents.byGroup[group.id] || []}
              onToggleCollapse={() => handleToggleCollapse(group.id)}
              onEdit={() => handleEditGroup(group)}
              onDelete={() => handleDeleteGroup(group.id)}
              onDrop={(agentId) => handleDrop(group.id, agentId)}
              onDragStart={() => {}}
              onOpenAgent={openAgent}
              sessionSummaries={sessionSummaries}
            />
          ))}

          {/* Ungrouped Agents Section */}
          {groupedAgents.ungrouped.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Ungrouped Agents</h3>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const agentId = e.dataTransfer.getData('text/plain');
                  if (agentId) handleDrop(null, agentId);
                }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg"
              >
                {groupedAgents.ungrouped.map((agent) => (
                  <div
                    key={agent.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', agent.id);
                    }}
                    className="cursor-move"
                  >
                    <AgentStatusCard agent={agent} sessionSummary={sessionSummaries[agent.id]} onClick={openAgent} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {groups.length === 0 && groupedAgents.ungrouped.length === 0 && (
            <EmptyState
              title="No groups yet"
              description="Create your first group to organize agents by team, function, or project."
              action={
                <button
                  onClick={handleCreateGroup}
                  className="text-sm text-ldaja-blue hover:underline"
                >
                  Create a group
                </button>
              }
            />
          )}
        </div>
      )}

      {/* Agent Group Modal */}
      <AgentGroupModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingGroup(null);
        }}
        onSave={handleSaveGroup}
        group={editingGroup}
        nextOrder={groups.length}
      />
    </div>
  );
}
